'use client';

import type { Swatch } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import { useState } from 'react';
import { MissingImage } from '../ProductCard/MissingImage';
import { ProductLink } from '../ProductCard/ProductLink';
import { Tooltip } from './Tooltip';

type Props = {
  swatches: Swatch[];
  activeSwatch: string;
  showColorLabel?: boolean;
  swatchSize?: number;
  action?: string;
};

export const SwatchCollection = ({
  swatches,
  activeSwatch,
  showColorLabel = true,
  swatchSize = 48,
}: Props) => {
  const t = useTranslations('products');
  const uniqueSwatches = uniqBy(swatches, (s) => s.UPID);
  const selectedSwatch = swatches.find((s) => s.UPID === activeSwatch);
  const [showTooltipIndex, setShowTooltipIndex] = useState<number | null>(null);
  return (
    <div>
      {showColorLabel && (
        <div
          data-cy="product-color"
          className="ui-mb-1 ui-font-semibold ui-text-sm ui-text-gray-500"
        >
          {t('color')}:{' '}
          <span className="ui-font-normal ui-text-black">
            {selectedSwatch?.colorName}
          </span>
        </div>
      )}

      <ul
        data-cy="swatch-list"
        className="ui-flex ui-flex-wrap ui-gap-0.5 !ui-m-0 !ui-mt-2 ui-p-0 !ui-list-none"
      >
        {uniqueSwatches.map((swatch, index) => {
          const sku = swatch.sku.toLowerCase();
          const isActive = swatch.UPID === activeSwatch;
          const imageURL = swatch.imageURL;
          const colorName = swatch.colorName;
          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              onMouseLeave={() => setShowTooltipIndex(null)}
              onMouseEnter={() => setShowTooltipIndex(index)}
              key={`${colorName}-${sku}`}
              title={colorName}
              className="ui-relative"
            >
              <Tooltip tip={colorName} show={index === showTooltipIndex} />
              <ProductLink
                product={{ slug: swatch.slug, sku }}
                className="ui-block ui-leading-none"
              >
                {!imageURL && (
                  <MissingImage
                    style={{
                      height: `${swatchSize}px`,
                      width: `${swatchSize}px`,
                    }}
                    className={classNames(
                      'ui-p-0.5 ui-shrink-0 ui-border-solid ui-border-2 ui-border-transparent ui-rounded-md ui-fill-gray-400/50',
                      {
                        '!ui-border-[var(--cta-color,#030712)]': isActive,
                      }
                    )}
                  />
                )}
                {imageURL && (
                  <img
                    style={{
                      height: `${swatchSize}px`,
                      width: `${swatchSize}px`,
                    }}
                    className={classNames(
                      'ui-p-0.5 ui-shrink-0 ui-object-cover ui-object-center ui-border-solid ui-border-2 ui-border-transparent ui-rounded-md',
                      {
                        '!ui-border-[var(--cta-color,#030712)]': isActive,
                      }
                    )}
                    src={
                      imageURL.includes('res.cloudinary.com')
                        ? imageURL.replace(
                            'https://images.floorforce.com/Textures/',
                            ''
                          )
                        : imageURL
                    }
                    alt={colorName}
                    height={swatchSize}
                    width={swatchSize}
                  />
                )}
              </ProductLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
